import { useEffect, useState, useCallback } from "react";
import { Main } from "@/components/Main";
import { TableData } from "@/App/PaymentAdvances/Table";
import { AddBtn } from "@/App/PaymentAdvances/Add";
import {
  getPaymentAdvances,
  PaymentAdvanceFiltersParams,
} from "@/Api/paymentAdvances";
import { getClientsDataWithFilters } from "@/Api/clients";
import { getSellersDataWithFilters } from "@/Api/sellers";
import { PaymentAdvanceResponse } from "@/types/paymentAdvance";
import { QrCode } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { Mask } from "@/utils/mask";
import {
  PAYMENT_ADVANCE_STATUS,
  PAYMENT_ADVANCE_STATUS_LABELS,
} from "@/utils/constants";
import { If } from "@/components/If";
import { AsyncSearchableSelect } from "@/components/ui/async-searchable-select";
import { useAuth } from "@/hooks/auth";
import { TypeUser } from "@/types/userType";

interface Client {
  id: number;
  uuid: string;
  name: string;
  phone?: string;
}

interface Seller {
  id: number;
  uuid: string;
  user?: { name: string };
}

const initialListData: PaymentAdvanceResponse = {
  current_page: 1,
  data: [],
  first_page_url: "",
  from: 0,
  last_page: 1,
  last_page_url: "",
  next_page_url: null,
  path: "",
  per_page: 15,
  prev_page_url: null,
  to: 0,
  total: 0,
};

export const PageList = () => {
  const user = useAuth();
  const userSeller = user?.user?.seller;
  const isSellerUser = user?.user?.user_type_id === TypeUser.seller;

  const [listData, setListData] =
    useState<PaymentAdvanceResponse>(initialListData);
  const [state, setState] = useState("loading");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSeller, setSelectedSeller] = useState<Seller | null>(
    isSellerUser && userSeller
      ? { id: userSeller.id, uuid: "", user: { name: user?.user?.name || "" } }
      : null
  );

  const today = Mask.getLocalDate();
  const [filters, setFilters] = useState<PaymentAdvanceFiltersParams>({
    client_id: "all",
    seller_id: isSellerUser ? userSeller?.id?.toString() : "all",
    status: "all",
    id: "",
    sale_id: "",
    start_date: today,
    end_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split("T")?.[0],
  });

  const fetchData = async (
    page: number = 1,
    currentFilters: PaymentAdvanceFiltersParams = filters
  ) => {
    setState("loading");
    try {
      const { data, ok } = await getPaymentAdvances(page, currentFilters);
      if (ok && data?.response) {
        setListData(data.response);
      }
    } catch (error) {
      console.error("Error fetching payment advances:", error);
    } finally {
      setState("production");
    }
  };

  const searchClients = useCallback(async (searchTerm: string): Promise<Client[]> => {
    try {
      const isPhoneSearch = /^\d+$/.test(searchTerm.replace(/[\s\-()]/g, ''));
      const { data, ok } = await getClientsDataWithFilters({
        name: isPhoneSearch ? undefined : searchTerm,
        phone: isPhoneSearch ? searchTerm : undefined,
        per_page: 20,
        is_active: true,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching clients:", error);
      return [];
    }
  }, []);

  const searchSellers = useCallback(async (searchTerm: string): Promise<Seller[]> => {
    try {
      const { data, ok } = await getSellersDataWithFilters({
        name: searchTerm,
        per_page: 20,
        is_active: true,
      });
      if (ok && data?.response?.data) {
        return data.response.data;
      }
      return [];
    } catch (error) {
      console.error("Error searching sellers:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    fetchData(1, filters);
  }, []);

  const pageActions = {
    next: async () => {
      if (listData.current_page < listData.last_page) {
        await fetchData(listData.current_page + 1, filters);
      }
    },
    previous: async () => {
      if (listData.current_page > 1) {
        await fetchData(listData.current_page - 1, filters);
      }
    },
    goToPage: async (page: number) => {
      await fetchData(page, filters);
    },
  };

  const handleApplyFilter = () => {
    const updatedFilters = {
      ...filters,
      client_id: selectedClient ? selectedClient.id.toString() : "all",
      seller_id: isSellerUser
        ? userSeller?.id?.toString()
        : selectedSeller
        ? selectedSeller.id.toString()
        : "all",
    };
    setFilters(updatedFilters);
    fetchData(1, updatedFilters);
  };

  const handleClearFilter = () => {
    const clearedFilters: PaymentAdvanceFiltersParams = {
      client_id: "all",
      seller_id: isSellerUser ? userSeller?.id?.toString() : "all",
      status: "all",
      linked: "all",
      id: "",
      sale_id: "",
      start_date: today,
      end_date: today,
      order_by: undefined,
      order_direction: undefined,
    };
    setFilters(clearedFilters);
    setSelectedClient(null);
    if (!isSellerUser) {
      setSelectedSeller(null);
    }
    fetchData(1, clearedFilters);
  };

  const handleSort = (column: string) => {
    const newDirection =
      filters.order_by === column && filters.order_direction === "asc"
        ? "desc"
        : "asc";

    const updatedFilters = {
      ...filters,
      order_by: column,
      order_direction: newDirection as "asc" | "desc",
    };
    setFilters(updatedFilters);
    fetchData(1, updatedFilters);
  };

  return (
    <Main>
      <PageHeader
        icon={QrCode}
        title="Pix Gerados"
        description="Gerencie os pagamentos antecipados"
      >
        <If condition={true}>
          <AddBtn updateFront={setListData} />
        </If>
      </PageHeader>
      <div className="flex flex-col gap-4">
        <div className="bg-white dark:bg-[#2c2c2c] p-4 rounded-lg border">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-end">
            <div className="space-y-2">
              <Label>Cliente</Label>
              <AsyncSearchableSelect<Client>
                fetchItems={searchClients}
                displayField={(client) =>
                  client.phone
                    ? `${client.name} - ${Mask.maskPhone(client.phone)}`
                    : client.name
                }
                placeholder="Buscar cliente..."
                searchPlaceholder="Digite o nome ou telefone..."
                emptyMessage="Nenhum cliente encontrado."
                value={selectedClient}
                onSelect={setSelectedClient}
                getItemId={(item) => item.id}
              />
            </div>

            {isSellerUser ? (
              <div className="space-y-2">
                <Label>Vendedor</Label>
                <Input
                  value={user?.user?.name || ""}
                  disabled
                  className="bg-gray-100"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Vendedor</Label>
                <AsyncSearchableSelect<Seller>
                  fetchItems={searchSellers}
                  displayField={(seller) => seller.user?.name || `Vendedor #${seller.id}`}
                  placeholder="Buscar vendedor..."
                  searchPlaceholder="Digite o nome..."
                  emptyMessage="Nenhum vendedor encontrado."
                  value={selectedSeller}
                  onSelect={setSelectedSeller}
                  getItemId={(item) => item.id}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="filter-status">Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, status: value }))
                }
              >
                <SelectTrigger id="filter-status">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  {Object.values(PAYMENT_ADVANCE_STATUS).map((value) => (
                    <SelectItem key={value} value={value}>
                      {PAYMENT_ADVANCE_STATUS_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-linked">Linkado</Label>
              <Select
                value={filters.linked || "all"}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, linked: value }))
                }
              >
                <SelectTrigger id="filter-linked">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="true">Linkado</SelectItem>
                  <SelectItem value="false">Não Linkado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-id">ID do Pix</Label>
              <Input
                id="filter-id"
                type="number"
                placeholder="Ex: 123"
                value={filters.id || ""}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, id: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-sale-id">ID Venda Vinculada</Label>
              <Input
                id="filter-sale-id"
                type="number"
                placeholder="Ex: 12345"
                value={filters.sale_id || ""}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, sale_id: e.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-start-date">Data Início</Label>
              <Input
                id="filter-start-date"
                type="date"
                value={filters.start_date}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    start_date: e.target.value,
                  }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="filter-end-date">Data Fim</Label>
              <Input
                id="filter-end-date"
                type="date"
                value={filters.end_date}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, end_date: e.target.value }))
                }
              />
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleApplyFilter}
                className={cn(colors.bg_primary, "flex-1")}
              >
                Filtrar
              </Button>
              <Button
                variant="outline"
                onClick={handleClearFilter}
                className="flex-1"
              >
                Limpar
              </Button>
            </div>
          </div>
        </div>

        {state === "loading" ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <TableData
            dataApi={listData}
            pageActions={pageActions}
            updateFront={setListData}
            onSort={handleSort}
            sortColumn={filters.order_by}
            sortDirection={filters.order_direction}
            onRefresh={() => fetchData(listData.current_page, filters)}
          />
        )}
      </div>
    </Main>
  );
};

export default PageList;
